import React, { useLayoutEffect, useMemo, useState } from 'react';

import { Select } from '@aline/common';
import { link, routes } from '@aline/routes';
import {
  Blocks,
  BookOpenText,
  Bot,
  FileChartColumnIncreasing,
  FilePenLine,
  LayoutList,
  MessageSquareText,
} from 'lucide-react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { AlineTooltip, SelectButton } from 'src/components';
import { SubmitFieldPlusIcon } from 'src/components/icons/SubmitFieldPlusIcon';
import { useWalkthroughActive } from 'src/editor/toolbars/editor-app-bars';
import { useCurrentUserQuery } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import {
  getIsApiDeckConnectionCallable,
  useGetVariablesForDocument,
  useUserIntegrations,
} from 'src/hooks';
import { useGetCurrentDoc } from 'src/hooks/document';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { useEditorLoading } from 'src/layout-engine';
import { useIsFocusedView } from 'src/layouts/focused-view';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  railCrmIcon: {
    display: 'block',
    height: 24,
    objectFit: 'contain',
    width: 24,
  },
  toggleButtonContainer: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderRadius: '12px 0 0 12px',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: theme.spacing(1, 0.5),
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

type ButtonValue =
  | 'outline'
  | 'add-input'
  | 'comments'
  | 'tracked-changes'
  | 'document-summary'
  | 'playbooks'
  | 'integrations'
  | `integration:${Apollo.Integration}`
  | 'ai-chat';

const INTEGRATION_VALUES = new Set<string>(Object.values(Apollo.Integration));

function getIsIntegration(value: string): value is Apollo.Integration {
  return INTEGRATION_VALUES.has(value);
}

type RailConnection = Pick<
  Apollo.ApiDeckConnection,
  'icon' | 'integration' | 'name' | 'state' | '__typename'
>;

function getEditorRailIntegrations({
  connections,
  documentIntegrations,
}: {
  connections: RailConnection[];
  documentIntegrations: readonly Apollo.Integration[];
}) {
  const connectionByIntegration = new Map(
    connections.map(connection => [connection.integration, connection])
  );
  const callableIntegrations = connections.flatMap(connection =>
    getIsApiDeckConnectionCallable(connection) ? [connection.integration] : []
  );
  const integrations = [...new Set([...callableIntegrations, ...documentIntegrations])];

  return integrations.map(integration => {
    const connection = connectionByIntegration.get(integration);
    return {
      icon: connection?.icon ?? null,
      integration,
      name: connection?.name || INTEGRATION_DISPLAY_NAME[integration],
    };
  });
}

/** Maps simple button values to their link helper. */
const BUTTON_TO_LINK: Record<string, (params: { documentId: string }) => string> = {
  'add-input': p => link.DocumentDetailAddInput(p),
  comments: p => link.DocumentDetailComments(p),
  'document-summary': p => link.DocumentDetailSummary(p),
  integrations: p => link.DocumentDetailIntegrations(p),
  outline: p => link.DocumentDetailFields(p),
  playbooks: p => link.DocumentDetailPlaybooks(p),
  'tracked-changes': p => link.DocumentDetailTrackedChanges(p),
};

/**
 * Maps button values to drawer route paths using type-safe link helpers.
 */
function getDrawerPath(documentId: string, buttonValue: NonNullable<ButtonValue>): string {
  const linkFn = BUTTON_TO_LINK[buttonValue];
  if (linkFn) return linkFn({ documentId });

  // Handle integration:XXX cases
  if (buttonValue.startsWith('integration:')) {
    const integration = buttonValue.replace('integration:', '');
    return link.DocumentDetailIntegrationDetail({ documentId, integration });
  }
  return link.DocumentDetail({ documentId });
}

// The bare document route opens the AI chat panel, as does its explicit AI route.
function getIsAiDrawerRoute(pathname: string): boolean {
  return Boolean(
    matchPath(routes.DocumentDetail, pathname) || matchPath(routes.DocumentDetailAi, pathname)
  );
}

/**
 * Determines which button is currently active based on the URL. A focused view has
 * no AI chat, so its fallback is Comments.
 */
function useActiveDrawerButton(isFocused: boolean): ButtonValue {
  const location = useLocation();
  const pathname = location.pathname;

  if (matchPath(routes.DocumentDetailFields, pathname)) return 'outline';
  if (matchPath(routes.DocumentDetailAddInput, pathname)) return 'add-input';
  if (matchPath(routes.DocumentDetailComments, pathname)) return 'comments';
  if (matchPath(routes.DocumentDetailTrackedChanges, pathname)) return 'tracked-changes';
  if (matchPath(routes.DocumentDetailSummary, pathname)) return 'document-summary';
  if (matchPath(routes.DocumentDetailPlaybooks, pathname)) return 'playbooks';

  // Check for specific integration routes
  const integrationMatch = matchPath(routes.DocumentDetailIntegrationDetail, pathname);
  const integration = integrationMatch?.params.integration;
  if (integration && getIsIntegration(integration)) {
    return `integration:${integration}`;
  }

  if (matchPath(routes.DocumentDetailIntegrations, pathname)) return 'integrations';

  return isFocused ? 'comments' : 'ai-chat';
}

interface EditorRightToolbarButtonsProps {
  isDocumentVersion: boolean;
  isFocused: boolean;
  isNonEditableDocument: boolean;
}

const EditorRightToolbarButtons = React.memo(function EditorRightToolbarButtons({
  isDocumentVersion,
  isFocused,
  isNonEditableDocument,
}: EditorRightToolbarButtonsProps): JSX.Element | null {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { documentId } = useDocumentParams();
  const activeButton = useActiveDrawerButton(isFocused);
  const { isOpen, openDrawer } = useGlobalDrawer();
  const { walkThroughActive } = useWalkthroughActive();
  const isOnAiDrawerRoute = getIsAiDrawerRoute(location.pathname);
  const [tooltipOpen, setTooltipOpen] = useState<ButtonValue | null>(null);
  const areCommentsHidden = walkThroughActive || isNonEditableDocument || !isDocumentVersion;

  // Auto-navigate to fields when drawer is open during walkthrough with no specific panel active.
  useLayoutEffect(() => {
    if (!activeButton && isOpen && walkThroughActive) {
      navigate(getDrawerPath(documentId, 'outline'), { replace: true });
    }
  }, [activeButton, documentId, isOpen, navigate, walkThroughActive]);

  // The editor opens on the AI chat panel, which a focused view does not offer.
  useLayoutEffect(() => {
    if (!isFocused || !isOnAiDrawerRoute) return;
    navigate(getDrawerPath(documentId, areCommentsHidden ? 'outline' : 'comments'), {
      replace: true,
    });
  }, [areCommentsHidden, documentId, isFocused, isOnAiDrawerRoute, navigate]);

  const buttons = useMemo(() => {
    return new Map<
      NonNullable<ButtonValue>,
      {
        dataCy: string;
        hide: boolean;
        icon: JSX.Element;
        title: string;
      }
    >([
      [
        'ai-chat',
        {
          dataCy: 'ai-chat-drawer-button',
          hide: isFocused,
          icon: <Bot size={24} strokeWidth={1.5} />,
          title: 'AI chat',
        },
      ],
      [
        'playbooks',
        {
          dataCy: 'playbooks-drawer-button',
          hide: walkThroughActive || isFocused,
          icon: <BookOpenText size={24} strokeWidth={1.5} />,
          title: 'Playbooks',
        },
      ],
      [
        'outline',
        {
          dataCy: Select.document.outlineButton,
          hide: false,
          icon: <LayoutList size={24} strokeWidth={1.5} />,
          title: 'Outline',
        },
      ],
      [
        'add-input',
        {
          dataCy: Select.document.addContentButton,
          hide: walkThroughActive || isNonEditableDocument || isFocused,
          icon: <SubmitFieldPlusIcon />,
          title: 'Add content',
        },
      ],
      [
        'comments',
        {
          dataCy: Select.document.commentsButton,
          hide: areCommentsHidden,
          icon: <MessageSquareText size={24} strokeWidth={1.5} />,
          title: 'Comments',
        },
      ],
      [
        'tracked-changes',
        {
          dataCy: Select.document.trackedChangesButton,
          hide: walkThroughActive || isNonEditableDocument || !isDocumentVersion,
          icon: <FilePenLine size={24} strokeWidth={1.5} />,
          title: 'Tracked changes',
        },
      ],
      [
        'document-summary',
        {
          dataCy: Select.document.documentSummaryButton.panel.container,
          hide: isFocused,
          icon: <FileChartColumnIncreasing size={24} strokeWidth={1.5} />,
          title: 'Document analysis',
        },
      ],
    ]);
  }, [areCommentsHidden, isDocumentVersion, isFocused, isNonEditableDocument, walkThroughActive]);

  function handleButtonClick(clickedValue: NonNullable<ButtonValue>): void {
    const targetPath = getDrawerPath(documentId, clickedValue);

    if (!isOpen) {
      // Drawer is closed, open it and navigate to the target
      navigate(targetPath, { replace: true });
      openDrawer();
      return;
    }

    if (activeButton === clickedValue) return;

    // Different button clicked, navigate to new content (drawer stays open)
    navigate(targetPath, { replace: true });
  }

  function handleTooltipOpen(buttonName: ButtonValue): void {
    setTooltipOpen(buttonName);
  }

  function handleTooltipClose(): void {
    setTooltipOpen(null);
  }

  if (walkThroughActive) return null;

  return (
    <div className={classes.toggleButtonContainer}>
      {buttons
        .entries()
        .map(([buttonName, { dataCy = `${buttonName}-drawer-button`, hide, icon, title }]) => {
          if (hide) return null;

          return (
            <AlineTooltip
              key={buttonName}
              arrow
              open={tooltipOpen === buttonName}
              placement="left"
              title={title}
              onClose={handleTooltipClose}
              onOpen={() => handleTooltipOpen(buttonName)}
            >
              <div>
                <SelectButton
                  aria-label={title}
                  data-cy={dataCy}
                  selected={isOpen && activeButton === buttonName}
                  variant="rightToolbar"
                  onClick={() => handleButtonClick(buttonName)}
                >
                  {icon}
                </SelectButton>
              </div>
            </AlineTooltip>
          );
        })
        .toArray()}
      {!isFocused && (
        <IntegrationNavIcons
          activeButton={activeButton}
          isOpen={isOpen}
          tooltipOpen={tooltipOpen}
          walkThroughActive={walkThroughActive}
          onButtonClick={handleButtonClick}
          onTooltipClose={handleTooltipClose}
          onTooltipOpen={handleTooltipOpen}
        />
      )}
    </div>
  );
});

/**
 * Editor right toolbar buttons that use URL navigation for the global drawer.
 * Replaces the old Redux-based drawer state management with route-based navigation.
 */
export function EditorRightToolbar(): JSX.Element | null {
  const editorLoading = useEditorLoading();
  const isFocused = useIsFocusedView();
  const { isDocumentVersion, isNonEditableDocument, loading } = useCheckUserEditorAccess();

  if (loading || editorLoading) return null;

  return (
    <EditorRightToolbarButtons
      isDocumentVersion={isDocumentVersion}
      isFocused={isFocused}
      isNonEditableDocument={isNonEditableDocument}
    />
  );
}

interface IntegrationNavIconsProps {
  activeButton: ButtonValue;
  isOpen: boolean;
  onButtonClick: (buttonValue: NonNullable<ButtonValue>) => void;
  onTooltipClose: () => void;
  onTooltipOpen: (buttonName: ButtonValue) => void;
  tooltipOpen: ButtonValue | null;
  walkThroughActive: boolean;
}

function IntegrationNavIcons({
  activeButton,
  isOpen,
  onButtonClick,
  onTooltipClose,
  onTooltipOpen,
  tooltipOpen,
  walkThroughActive,
}: IntegrationNavIconsProps): JSX.Element | null {
  const { classes } = useStyles();
  const { documentId } = useDocumentParams();
  const { allConnections } = useUserIntegrations();
  const { variables } = useGetVariablesForDocument(documentId);
  const railIntegrations = getEditorRailIntegrations({
    connections: allConnections,
    documentIntegrations: [
      ...new Set(
        variables.flatMap(v =>
          v.integrationLink?.integration ? [v.integrationLink.integration] : []
        )
      ),
    ],
  });

  if (walkThroughActive) return null;

  return (
    <>
      {railIntegrations.map(railIntegration => {
        const integrationString: NonNullable<ButtonValue> = `integration:${railIntegration.integration}`;

        return (
          <AlineTooltip
            key={railIntegration.integration}
            arrow
            placement="left"
            title={railIntegration.name}
          >
            <div>
              <SelectButton
                aria-label={railIntegration.name}
                aria-pressed={isOpen && activeButton === integrationString}
                selected={isOpen && activeButton === integrationString}
                variant="rightToolbar"
                onClick={() => onButtonClick(integrationString)}
              >
                <RailCrmIcon
                  className={classes.railCrmIcon}
                  icon={railIntegration.icon}
                  integration={railIntegration.integration}
                />
              </SelectButton>
            </div>
          </AlineTooltip>
        );
      })}
      <AlineTooltip
        arrow
        open={tooltipOpen === 'integrations'}
        placement="left"
        title="Integrations"
        onClose={onTooltipClose}
        onOpen={() => onTooltipOpen('integrations')}
      >
        <div>
          <SelectButton
            selected={isOpen && activeButton === 'integrations'}
            variant="rightToolbar"
            onClick={() => onButtonClick('integrations')}
          >
            <Blocks size={24} strokeWidth={1.5} />
          </SelectButton>
        </div>
      </AlineTooltip>
    </>
  );
}

function RailCrmIcon({
  className,
  icon,
  integration,
}: {
  className: string;
  icon: string | null;
  integration: Apollo.Integration;
}): JSX.Element {
  if (icon) return <img alt="" className={className} src={icon} />;
  return <CrmIntegrationIcon className={className} integration={integration} />;
}

function useCheckUserEditorAccess(): {
  isDocumentVersion: boolean;
  isNonEditableDocument: boolean;
  loading: boolean;
} {
  const { document, loading: loadingDocument } = useGetCurrentDoc();
  const { loading: loadingUser } = useCurrentUserQuery();
  const isDocumentVersion = document?.latestVersion.__typename === 'DocumentVersion';
  const wasSentForSig = getWorkflowWasSentForSig(document?.workflow ?? null);
  const isNonEditableDocument = Boolean(
    document?.isSigned || document?.isCertificate || wasSentForSig
  );

  return {
    isDocumentVersion,
    isNonEditableDocument,
    loading: loadingUser || loadingDocument,
  };
}
